import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAtom } from "jotai";
import { languageAtom, receptionButtonTypeAtom, receptionCompanyIdAtom } from "~/Atom";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import ReceptionHeader from "~/components/ReceptionHeader";
import { useCallingPhone } from "~/components/hooks/useCallingPhone";
import { getAppSettingsData, getPhoneBooksEntity } from "~/utils/receptionDB";
import { mapReceptionLanguage, useInactivityTimeout } from "~/utils/receptionUtils";
import type { CallInfoEntity, MenuWords, SettingMenuButtonEntity } from "~/utils/receptionDB.types";
import {
  postVisitHistory,
  type ChatRequestType,
  type MenuButtonType,
} from "~/services/visitHistoryApi";
import { ROUTE } from "~/constants/routes";
import { IoCall } from "~/Icons";
import "~/css/reception/CallingPhonePage.css";

type AppointmentData = {
  data: {
    notificationType: string;
    groupNames: { languageCode: string; groupName: string }[] | null;
    names: { languageCode: string; name: string }[] | null;
    callType: string;
    extensionNumber: string;
    faceTimeAddress: string;
    isChatSuccess: boolean;
    appointmentId: number;
    appointmentVisitorIds: number[];
  };
};

/** CallingPhonePage の location.state の型 */
type CallingPhoneState = {
  appointment?: AppointmentData | null;
  companyName?: string | null;
  visitorName?: string | null;
  requirement?: string | null;
  groupName?: string | null;
  userName?: string | null;
  extensionNumber?: string | null;
};

type MenuButtonCallSettingType = "recruit" | "delivery" | "other" | "taxi" | "secretary";

type MenuButtonCallSettingKeys = {
  notificationTypeKey:
    | "recruitNotificationType"
    | "deliveryNotificationType"
    | "otherNotificationType"
    | "taxiNotificationType"
    | "secretaryNotificationType";
  callTypeKey:
    | "recruitCallType"
    | "deliveryCallType"
    | "otherCallType"
    | "taxiCallType"
    | "secretaryCallType";
  extensionNumberKey:
    | "recruitExtensionNumber"
    | "deliveryExtensionNumber"
    | "otherExtensionNumber"
    | "taxiExtensionNumber"
    | "secretaryExtensionNumber";
};

const BUTTON_TYPE_TO_CALL_SETTING_KEYS: Record<
  MenuButtonCallSettingType,
  MenuButtonCallSettingKeys
> = {
  recruit: {
    notificationTypeKey: "recruitNotificationType",
    callTypeKey: "recruitCallType",
    extensionNumberKey: "recruitExtensionNumber",
  },
  delivery: {
    notificationTypeKey: "deliveryNotificationType",
    callTypeKey: "deliveryCallType",
    extensionNumberKey: "deliveryExtensionNumber",
  },
  other: {
    notificationTypeKey: "otherNotificationType",
    callTypeKey: "otherCallType",
    extensionNumberKey: "otherExtensionNumber",
  },
  taxi: {
    notificationTypeKey: "taxiNotificationType",
    callTypeKey: "taxiCallType",
    extensionNumberKey: "taxiExtensionNumber",
  },
  secretary: {
    notificationTypeKey: "secretaryNotificationType",
    callTypeKey: "secretaryCallType",
    extensionNumberKey: "secretaryExtensionNumber",
  },
};

// MenuWordsのstring型キーのみを導出（boolean型キーを除外）
type MenuWordsStringKey = {
  [K in keyof MenuWords]: MenuWords[K] extends string ? K : never;
}[keyof MenuWords];

const BUTTON_TYPE_TO_MENU_WORDS_KEY: Record<string, MenuWordsStringKey> = {
  recruit: "recruitButtonTitle",
  delivery: "deliveryButtonTitle",
  taxi: "taxiButtonTitle",
  secretary: "secretaryButtonTitle",
  other: "otherButtonTitle",
};

const CALL_INFO_MISSING_ERROR_CODE = "A2006";
const INVALID_CALL_SETTINGS_ERROR_CODE = "A2008";
const EXTENSION_NUMBER_RESOLUTION_ERROR_CODE = "A2009";

const createCallInfoError = (errorCode: string, message: string): Error => {
  const error = new Error(message);
  error.name = errorCode;
  return error;
};

const navigateForCallInfoError = (navigate: ReturnType<typeof useNavigate>, err: unknown): void => {
  if (
    err instanceof Error &&
    (err.name === CALL_INFO_MISSING_ERROR_CODE ||
      err.name === INVALID_CALL_SETTINGS_ERROR_CODE ||
      err.name === EXTENSION_NUMBER_RESOLUTION_ERROR_CODE)
  ) {
    navigate(`${ROUTE.RECEPTION.ERROR}?code=${err.name}`);
    return;
  }

  navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
};

const isMenuButtonCallSettingType = (
  buttonType: string | null
): buttonType is MenuButtonCallSettingType => {
  return (
    buttonType === "recruit" ||
    buttonType === "delivery" ||
    buttonType === "other" ||
    buttonType === "taxi" ||
    buttonType === "secretary"
  );
};

const isCallableNotificationType = (notificationType: string): boolean => {
  return notificationType === "CALL" || notificationType === "CHAT_CALL";
};

const resolveMenuButton = (
  menuButtons: SettingMenuButtonEntity[],
  companyId: number
): SettingMenuButtonEntity | undefined => {
  return menuButtons.find((item) => item.id === companyId);
};

const resolveExtensionNumberFromAppSetting = (
  menuButton: SettingMenuButtonEntity,
  buttonType: MenuButtonCallSettingType
): string => {
  const { notificationTypeKey, callTypeKey, extensionNumberKey } =
    BUTTON_TYPE_TO_CALL_SETTING_KEYS[buttonType];
  const notificationType = menuButton[notificationTypeKey];
  const callType = menuButton[callTypeKey];
  const extensionNumber = menuButton[extensionNumberKey];

  if (!isCallableNotificationType(notificationType) || callType !== "PHONE" || !extensionNumber) {
    throw createCallInfoError(
      INVALID_CALL_SETTINGS_ERROR_CODE,
      `Invalid call settings for buttonType=${buttonType}`
    );
  }

  return extensionNumber;
};

const resolveAppointmentExtensionNumber = (
  buttonType: string | null,
  appointment: AppointmentData | null
): string | undefined => {
  if (buttonType !== "qr" && buttonType !== "receptionCode") {
    return undefined;
  }

  const appointmentExtensionNumber = appointment?.data.extensionNumber;
  if (!appointmentExtensionNumber) {
    throw createCallInfoError(
      EXTENSION_NUMBER_RESOLUTION_ERROR_CODE,
      `appointment extensionNumber is required for buttonType=${buttonType}`
    );
  }

  return appointmentExtensionNumber;
};

// ボタン種別に応じて appointment / location.state / アプリ設定から内線番号を解決する処理
const resolveExtensionNumber = (
  menuButtons: SettingMenuButtonEntity[],
  companyId: number,
  buttonType: string | null,
  appointment: AppointmentData | null,
  pageState: CallingPhoneState | null
): string => {
  const appointmentExtensionNumber = resolveAppointmentExtensionNumber(buttonType, appointment);
  if (appointmentExtensionNumber) {
    return appointmentExtensionNumber;
  }

  const menuButton = resolveMenuButton(menuButtons, companyId);
  if (!menuButton) {
    throw createCallInfoError(
      EXTENSION_NUMBER_RESOLUTION_ERROR_CODE,
      "menuButton could not be resolved"
    );
  }

  if (buttonType === "other" && menuButton.isDisplayInputExtensionNumber) {
    const inputExtensionNumber = pageState?.extensionNumber;
    if (!inputExtensionNumber) {
      throw createCallInfoError(
        EXTENSION_NUMBER_RESOLUTION_ERROR_CODE,
        "location.state.extensionNumber is required for other buttonType"
      );
    }
    return inputExtensionNumber;
  }

  if (!isMenuButtonCallSettingType(buttonType)) {
    throw createCallInfoError(
      EXTENSION_NUMBER_RESOLUTION_ERROR_CODE,
      `Unsupported buttonType=${String(buttonType)}`
    );
  }

  return resolveExtensionNumberFromAppSetting(menuButton, buttonType);
};

// AppSettingsNormalized の menuWords から buttonType に対応するラベルを取得するヘルパーです
const resolveButtonLabel = (
  menuWords: {
    id: number;
    items: { languageCode: string; menuWords: MenuWords }[];
  }[],
  companyId: number,
  buttonType: string,
  language: string
): string => {
  const menuWordsKey: MenuWordsStringKey | undefined = BUTTON_TYPE_TO_MENU_WORDS_KEY[buttonType];
  const langCode = mapReceptionLanguage(language);
  if (!menuWordsKey || !langCode) return "";
  const wordEntity = menuWords.find((w) => w.id === companyId);
  return wordEntity?.items.find((i) => i.languageCode === langCode)?.menuWords[menuWordsKey] ?? "";
};

// other かつ isDisplayInputExtensionNumber=true の場合は extensionNumber を、それ以外はボタン名を返す
const resolveDisplayLabel = (
  appSettings: {
    menuWords: Parameters<typeof resolveButtonLabel>[0];
    menuButtons: { id: number; isDisplayInputExtensionNumber: boolean }[];
  },
  companyId: number,
  buttonType: string,
  language: string,
  extensionNumber: string | undefined
): string => {
  if (buttonType === "other" && extensionNumber) {
    const menuButton = appSettings.menuButtons.find((m) => m.id === companyId);
    if (menuButton?.isDisplayInputExtensionNumber) return extensionNumber;
  }
  return resolveButtonLabel(appSettings.menuWords, companyId, buttonType, language);
};

// receptionCommonWord から callingMessage を解決するヘルパー
const resolveCallingMessage = (
  receptionCommonWord:
    | {
        items: {
          languageCode: string;
          screenWords: { callingMessage: string };
        }[];
      }
    | undefined,
  language: string
): string => {
  const langCode = mapReceptionLanguage(language);
  return (
    receptionCommonWord?.items.find((i) => i.languageCode === langCode)?.screenWords
      .callingMessage ?? ""
  );
};

// receptionCommonWord から callConnectingMessage を解決するヘルパー
const resolveCallConnectingMessage = (
  receptionCommonWord:
    | {
        items: {
          languageCode: string;
          screenWords: { callConnectingMessage: string };
        }[];
      }
    | undefined,
  language: string
): string => {
  const langCode = mapReceptionLanguage(language);
  return (
    receptionCommonWord?.items.find((i) => i.languageCode === langCode)?.screenWords
      .callConnectingMessage ?? ""
  );
};

// qr/receptionCode ボタンタイプ用: appointmentからグループ名・社員名を解決する
const resolveQrReceptionLabel = (
  appointment: AppointmentData,
  language: string
): { nameLabel: string; groupLabel: string } => {
  const langCode = mapReceptionLanguage(language);
  const groupName =
    appointment.data.groupNames?.find((g) => g.languageCode === langCode)?.groupName ?? "";
  if (!appointment.data.names) {
    return { nameLabel: groupName, groupLabel: "" };
  }
  const name = appointment.data.names.find((n) => n.languageCode === langCode)?.name ?? "";
  return { nameLabel: name, groupLabel: groupName };
};

// buttonType に応じた notificationType を SettingMenuButtonEntity から取得するヘルパー
const resolveNotificationType = (
  menuButtons: SettingMenuButtonEntity[],
  companyId: number,
  buttonType: string | null
): string | null => {
  if (!buttonType) return null;
  const menuButton = menuButtons.find((m) => m.id === companyId);
  if (!menuButton) return null;
  const map: Record<string, string | undefined> = {
    recruit: menuButton.recruitNotificationType,
    delivery: menuButton.deliveryNotificationType,
    other: menuButton.otherNotificationType,
    taxi: menuButton.taxiNotificationType,
    secretary: menuButton.secretaryNotificationType,
  };
  return map[buttonType] ?? null;
};

// isChatSuccess を ChatRequestType に変換するヘルパー
const toChatRequestType = (isChatSuccess: boolean | undefined): ChatRequestType => {
  if (isChatSuccess === true) return "success";
  if (isChatSuccess === false) return "failed";
  return "noRequest";
};

// buttonType/companyId に応じてラベルをセットするヘルパー
const resolveAndSetButtonLabel = (
  appSettings: Parameters<typeof resolveDisplayLabel>[0],
  companyId: number,
  buttonType: string | null,
  language: string,
  extensionNumber: string | undefined,
  appointment: AppointmentData | null,
  setButtonLabel: (v: string) => void,
  setButtonGroupLabel: (v: string) => void
): void => {
  if (!buttonType) return;
  if ((buttonType === "qr" || buttonType === "receptionCode") && appointment) {
    const { nameLabel, groupLabel } = resolveQrReceptionLabel(appointment, language);
    setButtonLabel(nameLabel);
    setButtonGroupLabel(groupLabel);
  } else {
    setButtonLabel(
      resolveDisplayLabel(appSettings, companyId, buttonType, language, extensionNumber)
    );
  }
};

const STATUS_MESSAGE_FALLBACK = {
  idle: "通話ボタンを押してください",
  waitingWithoutButton: "接続中...",
  waitingWithButton: "接続中...",
  calling: "画面に向かってお話しください",
} as const;

type CallingScreenDesignType = keyof typeof STATUS_MESSAGE_FALLBACK;

interface CallingActionsProps {
  designType: CallingScreenDesignType;
  onTapCall: () => void;
  onTapHangUp: () => void;
}

interface PhoneActionButtonProps {
  kind: "call" | "callDisabled" | "hangUp";
  ariaLabel: string;
  onClick?: () => void;
  disabled?: boolean;
}

const PhoneActionButton: React.FC<PhoneActionButtonProps> = ({
  kind,
  ariaLabel,
  onClick,
  disabled = false,
}) => {
  const isHangUp = kind === "hangUp";

  return (
    <button
      type="button"
      className={`callingPhone__actionButton callingPhone__actionButton--${kind}`}
      onClick={onClick}
      aria-label={ariaLabel}
      data-reception-interactive={disabled ? undefined : "true"}
      disabled={disabled}
    >
      <IoCall
        className={`callingPhone__actionIcon${isHangUp ? " callingPhone__actionIcon--hangUp" : ""}`}
        aria-hidden="true"
      />
    </button>
  );
};

const CallingActions: React.FC<CallingActionsProps> = ({ designType, onTapCall, onTapHangUp }) => {
  if (designType === "idle") {
    return (
      <div className="callingPhone__actions" aria-live="polite">
        <PhoneActionButton kind="call" ariaLabel="発信する" onClick={onTapCall} />
      </div>
    );
  }

  if (designType === "waitingWithoutButton") {
    return (
      <div className="callingPhone__actions" aria-live="polite">
        <PhoneActionButton kind="callDisabled" ariaLabel="接続中" disabled />
      </div>
    );
  }

  return (
    <div className="callingPhone__actions" aria-live="polite">
      <PhoneActionButton kind="hangUp" ariaLabel="電話を切る" onClick={onTapHangUp} />
    </div>
  );
};

interface CallingPhoneViewProps {
  extensionNumber: string;
  callInfo: CallInfoEntity;
  completePath?: string;
  buttonLabel?: string;
  buttonGroupLabel?: string;
  callingMessage?: string;
  callConnectingMessage?: string;
  onCallStarted?: () => void;
  onPostVisitHistory?: () => Promise<void>;
}

const resolveScreenMessage = (
  designType: CallingScreenDesignType,
  callConnectingMessage?: string,
  callingMessage?: string
): string => {
  if (designType === "calling") {
    return callingMessage || STATUS_MESSAGE_FALLBACK.calling;
  }

  if (designType === "waitingWithoutButton" || designType === "waitingWithButton") {
    return callConnectingMessage || STATUS_MESSAGE_FALLBACK[designType];
  }

  return STATUS_MESSAGE_FALLBACK.idle;
};

const CallingMessage: React.FC<{
  designType: CallingScreenDesignType;
  callConnectingMessage?: string;
  callingMessage?: string;
}> = ({ designType, callConnectingMessage, callingMessage }) => (
  <p className="callingPhone__screenMessage">
    {resolveScreenMessage(designType, callConnectingMessage, callingMessage)}
  </p>
);

const ButtonLabelGroup: React.FC<{
  buttonLabel?: string;
  buttonGroupLabel?: string;
}> = ({ buttonLabel, buttonGroupLabel }) => {
  if (!buttonGroupLabel && !buttonLabel) return null;
  return (
    <div className="callingPhone__labelGroup">
      {buttonGroupLabel && <p className="callingPhone__buttonGroupLabel">{buttonGroupLabel}</p>}
      {buttonLabel && <p className="callingPhone__buttonLabel">{buttonLabel}</p>}
    </div>
  );
};

/**
 * 通話UIの描画担当。フック呼び出し順を保つために内部コンポーネントとして分離。
 */
const CallingPhoneView: React.FC<CallingPhoneViewProps> = ({
  extensionNumber,
  callInfo,
  completePath,
  buttonLabel,
  buttonGroupLabel,
  callingMessage,
  callConnectingMessage,
  onCallStarted,
  onPostVisitHistory,
}) => {
  const { designType, callDuration, onTapBack, onTapHangUp, onTapCall } = useCallingPhone({
    extensionNumber,
    callInfo,
    completePath,
    onPostVisitHistory,
  });

  const isCalling = designType === "calling";
  const targetAriaLabel = buttonLabel || buttonGroupLabel || "通話先";

  const handleTapCall = () => {
    onCallStarted?.();
    onTapCall();
  };

  return (
    <div className="calling-phone-page">
      <ReceptionHeader showBackButton onBackButtonClick={onTapBack} />
      <main className="callingPhone">
        <section className="callingPhone__content" aria-label={targetAriaLabel}>
          <ButtonLabelGroup buttonLabel={buttonLabel} buttonGroupLabel={buttonGroupLabel} />

          <div className="callingPhone__hero">
            <CallingActions
              designType={designType}
              onTapCall={handleTapCall}
              onTapHangUp={onTapHangUp}
            />

            <div className="callingPhone__messageGroup">
              <CallingMessage
                designType={designType}
                callConnectingMessage={callConnectingMessage}
                callingMessage={callingMessage}
              />

              <p
                className={`callingPhone__timer typo-h2${!isCalling ? " callingPhone__timer--hidden" : ""}`}
                aria-live="polite"
                aria-label={`通話時間 ${callDuration}`}
              >
                {callDuration}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

// appointment から来訪履歴パラメータを抽出するヘルパー
const extractAppointmentParams = (appointment: AppointmentData | null) => {
  const data = appointment?.data;
  return {
    chatRequestType: toChatRequestType(data?.isChatSuccess),
    appointmentId: data?.appointmentId ?? null,
    visitorIds: data?.appointmentVisitorIds ?? null,
  };
};

// pageState / ボタン情報から来訪履歴パラメータを抽出するヘルパー
const extractPageStateParams = (
  pageState: CallingPhoneState | null,
  buttonGroupLabel: string,
  buttonLabel: string
) => {
  const {
    groupName = null,
    userName = null,
    companyName = null,
    visitorName = null,
    requirement = null,
  } = pageState ?? {};
  return {
    buttonTitle: buttonLabel || null,
    groupName: (groupName ?? buttonGroupLabel) || null,
    userName,
    companyName,
    visitorName,
    requirement,
  };
};

// 来訪履歴登録APIに渡すパラメータを組み立てるヘルパー
const buildStateParams = (
  pageState: CallingPhoneState | null,
  appointment: AppointmentData | null,
  buttonGroupLabel: string,
  buttonLabel: string,
  buttonType: string | null,
  isAppointmentHistoryEnabled: boolean,
  notificationType: string | null
) => ({
  isCalling: true as const,
  isAppointmentHistoryEnabled,
  menuButtonType: (buttonType ?? "other") as MenuButtonType,
  notificationType,
  ...extractAppointmentParams(appointment),
  ...extractPageStateParams(pageState, buttonGroupLabel, buttonLabel),
});

/**
 * CallingPhone画面（通話設定と発信先番号をDB / location.state から解決する）
 */
const CallingPhonePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const [receptionButtonType] = useAtom(receptionButtonTypeAtom);
  const companyId = receptionCompanyId;
  const buttonType = receptionButtonType || null;
  const [language] = useAtom(languageAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });
  const pageState = (location.state as CallingPhoneState | null) ?? null;
  const completePath = ROUTE.RECEPTION.COMPLETE;
  const appointment = pageState?.appointment ?? null;
  const [callInfo, setCallInfo] = useState<CallInfoEntity | undefined>(undefined);
  const [extensionNumber, setExtensionNumber] = useState<string | undefined>(undefined);
  const [buttonLabel, setButtonLabel] = useState("");
  const [buttonGroupLabel, setButtonGroupLabel] = useState("");
  const [callingMessage, setCallingMessage] = useState("");
  const [callConnectingMessage, setCallConnectingMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCallStarted, setIsCallStarted] = useState(false);
  const [isAppointmentHistoryEnabled, setIsAppointmentHistoryEnabled] = useState(false);
  const [notificationType, setNotificationType] = useState<string | null>(null);

  useEffect(() => {
    const applyExtraSettings = async () => {
      const phoneBooksEntity = await getPhoneBooksEntity();
      setIsAppointmentHistoryEnabled(phoneBooksEntity?.isAppointmentHistoryEnabled ?? false);
    };
    void applyExtraSettings();
  }, []);

  useEffect(() => {
    const fetchCallInfo = async () => {
      try {
        const appSettings = await getAppSettingsData();
        if (!appSettings?.callInfo) {
          throw createCallInfoError(CALL_INFO_MISSING_ERROR_CODE, "callInfo is missing");
        }

        const nextExtensionNumber = resolveExtensionNumber(
          appSettings.menuButtons,
          companyId,
          buttonType,
          appointment,
          pageState
        );

        setCallInfo(appSettings.callInfo);
        setExtensionNumber(nextExtensionNumber);

        // notificationType を解決
        setNotificationType(
          appointment?.data.notificationType ??
            resolveNotificationType(appSettings.menuButtons, companyId, buttonType)
        );

        // receptionCommonWord からメッセージを取得
        setCallingMessage(resolveCallingMessage(appSettings.receptionCommonWord, language));
        setCallConnectingMessage(
          resolveCallConnectingMessage(appSettings.receptionCommonWord, language)
        );

        // buttonType と companyId からボタンラベルを解決
        resolveAndSetButtonLabel(
          appSettings,
          companyId,
          buttonType,
          language,
          nextExtensionNumber,
          appointment,
          setButtonLabel,
          setButtonGroupLabel
        );
      } catch (err) {
        console.error("Failed to load callInfo:", err);
        navigateForCallInfoError(navigate, err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchCallInfo();
  }, [navigate, companyId, buttonType, language, appointment, pageState]);

  useEffect(() => {
    if (isLoading) return;

    void playWithCooldown(callConnectingMessage).catch((err) => {
      console.error("callingPhone playVoice failed:", err);
    });
  }, [isLoading, playWithCooldown]);

  // 来訪履歴登録API呼び出しコールバック
  const handlePostVisitHistory = useCallback(async (): Promise<void> => {
    const params = buildStateParams(
      pageState,
      appointment,
      buttonGroupLabel,
      buttonLabel,
      buttonType,
      isAppointmentHistoryEnabled,
      notificationType
    );
    await postVisitHistory(params);
  }, [
    appointment,
    isAppointmentHistoryEnabled,
    buttonType,
    buttonLabel,
    buttonGroupLabel,
    notificationType,
    pageState,
  ]);

  const { stopTimer, startTimer } = useInactivityTimeout(navigate);

  // 発信前（idle）のみ無操作タイムアウトを有効化
  useEffect(() => {
    if (isLoading || isCallStarted) {
      stopTimer();
      return;
    }

    startTimer();
  }, [isLoading, isCallStarted, startTimer, stopTimer]);

  if (isLoading || !callInfo || !extensionNumber) {
    return null;
  }

  return (
    <CallingPhoneView
      extensionNumber={extensionNumber}
      callInfo={callInfo}
      completePath={completePath}
      buttonLabel={buttonLabel}
      buttonGroupLabel={buttonGroupLabel}
      callingMessage={callingMessage}
      callConnectingMessage={callConnectingMessage}
      onCallStarted={() => setIsCallStarted(true)}
      onPostVisitHistory={handlePostVisitHistory}
    />
  );
};

export default CallingPhonePage;
